import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { modsAPI, vehiclesAPI, enumsAPI, servicesAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Package,
  ChevronRight,
  Calendar,
  Gauge,
  DollarSign,
  ExternalLink,
  Search,
  Filter,
  X,
  Image,
  FileDown,
} from "lucide-react";

const TagBadge = ({ tag }) => {
  const colors = {
    Upgrade: "bg-green-500/20 text-green-500 border-green-500/50",
    Repair: "bg-blue-500/20 text-blue-500 border-blue-500/50",
    TrailFix: "bg-amber-500/20 text-amber-500 border-amber-500/50",
    Prototype: "bg-purple-500/20 text-purple-500 border-purple-500/50",
  };
  return (
    <span className={`rounded-sm font-mono uppercase text-xs font-bold px-2 py-0.5 border ${colors[tag] || "bg-zinc-500/20 text-zinc-500 border-zinc-500/50"}`}>
      {tag}
    </span>
  );
};

export default function BuildSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState(null);
  const [mods, setMods] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [vehicleRes, modsRes, categoriesRes, tagsRes, servicesRes] = await Promise.all([
        vehiclesAPI.get(id),
        modsAPI.list(id),
        enumsAPI.modCategories(),
        enumsAPI.modTags(),
        servicesAPI.list(id),
      ]);
      setVehicle(vehicleRes.data);
      setMods(modsRes.data);
      setServices(servicesRes.data);
      setCategories(categoriesRes.data);
      setAvailableTags(tagsRes.data);
    } catch (error) {
      toast.error("Failed to load build sheet");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  // Filtered mods
  const filteredMods = useMemo(() => {
    return mods.filter((mod) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          mod.item_name?.toLowerCase().includes(query) ||
          mod.brand?.toLowerCase().includes(query) ||
          mod.part_number?.toLowerCase().includes(query) ||
          mod.vendor?.toLowerCase().includes(query) ||
          mod.notes?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Category filter
      if (categoryFilter !== "all" && mod.category !== categoryFilter) {
        return false;
      }

      // Tag filter
      if (tagFilter !== "all") {
        if (!mod.tags || !mod.tags.includes(tagFilter)) {
          return false;
        }
      }

      return true;
    });
  }, [mods, searchQuery, categoryFilter, tagFilter]);

  // Group filtered mods by category
  const modsByCategory = useMemo(() => {
    return filteredMods.reduce((acc, mod) => {
      const cat = mod.category || "Other";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(mod);
      return acc;
    }, {});
  }, [filteredMods]);

  const clearFilters = () => {
    setSearchQuery("");
    setCategoryFilter("all");
    setTagFilter("all");
  };

  const hasActiveFilters = searchQuery || categoryFilter !== "all" || tagFilter !== "all";
  const totalCost = mods.reduce((sum, m) => sum + (m.cost || 0), 0);

  // PDF Export Function
  const exportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("BUILD SHEET", pageWidth / 2, 20, { align: "center" });
    
    // Vehicle Info
    doc.setFontSize(16);
    doc.text(vehicle.nickname || "Vehicle", pageWidth / 2, 30, { align: "center" });
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const vehicleDetails = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(" ");
    doc.text(vehicleDetails || "Vehicle Details", pageWidth / 2, 37, { align: "center" });
    
    // Summary
    doc.setFontSize(11);
    doc.text(`Total Mods: ${mods.length}  |  Total Invested: $${totalCost.toLocaleString()}`, pageWidth / 2, 47, { align: "center" });
    
    // Line separator
    doc.setDrawColor(200);
    doc.line(14, 52, pageWidth - 14, 52);
    
    let yPos = 60;
    
    // Group mods by category
    const groupedMods = mods.reduce((acc, mod) => {
      const cat = mod.category || "Other";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(mod);
      return acc;
    }, {});
    
    // Iterate through categories
    categories.forEach((category) => {
      const categoryMods = groupedMods[category];
      if (!categoryMods || categoryMods.length === 0) return;
      
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      // Category Header
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setFillColor(40, 40, 40);
      doc.rect(14, yPos - 5, pageWidth - 28, 8, "F");
      doc.setTextColor(255, 255, 255);
      doc.text(category.toUpperCase(), 16, yPos);
      doc.setTextColor(0, 0, 0);
      yPos += 10;
      
      // Mods table for this category
      const tableData = categoryMods.map((mod) => [
        mod.item_name || "",
        mod.brand || "-",
        mod.part_number || "-",
        mod.install_date ? new Date(mod.install_date + "T00:00:00").toLocaleDateString() : "-",
        mod.cost ? `$${mod.cost.toLocaleString()}` : "-",
        mod.tags?.join(", ") || "-",
      ]);
      
      autoTable(doc, {
        startY: yPos,
        head: [["Item", "Brand", "Part #", "Installed", "Cost", "Tags"]],
        body: tableData,
        theme: "striped",
        headStyles: { fillColor: [249, 115, 22], fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 30 },
          2: { cellWidth: 25 },
          3: { cellWidth: 22 },
          4: { cellWidth: 20 },
          5: { cellWidth: 30 },
        },
        margin: { left: 14, right: 14 },
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    });
    
    // Service History Section
    if (services.length > 0) {
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("SERVICE HISTORY", pageWidth / 2, yPos, { align: "center" });
      yPos += 5;
      doc.setDrawColor(200);
      doc.line(14, yPos, pageWidth - 14, yPos);
      yPos += 8;
      
      const totalServiceCost = services.reduce((sum, s) => sum + (s.cost || 0), 0);
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`Total Services: ${services.length}  |  Total Service Cost: $${totalServiceCost.toLocaleString()}`, pageWidth / 2, yPos, { align: "center" });
      yPos += 10;
      
      const serviceData = services
        .sort((a, b) => (b.service_date || "").localeCompare(a.service_date || ""))
        .map((s) => [
          s.title || s.category || "",
          s.category || "-",
          s.service_date ? new Date(s.service_date + "T00:00:00").toLocaleDateString() : "-",
          s.service_mileage ? s.service_mileage.toLocaleString() + " mi" : "-",
          s.cost ? `$${s.cost.toLocaleString()}` : "-",
          s.labor_type || "-",
        ]);
      
      autoTable(doc, {
        startY: yPos,
        head: [["Service", "Category", "Date", "Mileage", "Cost", "Labor"]],
        body: serviceData,
        theme: "striped",
        headStyles: { fillColor: [34, 139, 34], fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 45 },
          1: { cellWidth: 30 },
          2: { cellWidth: 22 },
          3: { cellWidth: 25 },
          4: { cellWidth: 20 },
          5: { cellWidth: 20 },
        },
        margin: { left: 14, right: 14 },
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text(
        `Generated on ${new Date().toLocaleDateString()} | Dynamite Off-Road Buildsheet Pro`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 10, { align: "right" });
    }
    
    // Save
    const filename = `${vehicle.nickname || "vehicle"}_buildsheet_${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(filename);
    toast.success("Build sheet exported as PDF");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!vehicle) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to={`/vehicles/${id}`}
                data-testid="back-to-dashboard-btn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <HeaderIcon />

                <div>
                  <h1 className="text-lg font-bold text-foreground">
                    Build Sheet
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    {vehicle.nickname}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {mods.length > 0 && (
                <Button
                  onClick={exportPDF}
                  data-testid="export-pdf-btn"
                  variant="outline"
                  className="btn-tactical border-zinc-700 hover:bg-zinc-800"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              )}
              <Button
                onClick={() => navigate(`/vehicles/${id}/mods/new`)}
                data-testid="add-mod-btn"
                className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Mod
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-48 sm:h-64 bg-zinc-900 overflow-hidden">
        {vehicle.primary_photo ? (
          <img
            src={vehicle.primary_photo}
            alt={vehicle.nickname}
            className="w-full h-full object-cover opacity-50"
          />
        ) : (
          <div
            className="w-full h-full bg-cover bg-center opacity-30"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1627666259372-54f2bf2622b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxvdmVybGFuZCUyMGplZXAlMjBjYW1waW5nJTIwc3Vuc2V0fGVufDB8fHx8MTc2ODgyODA5NHww&ixlib=rb-4.1.0&q=85')`,
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              {vehicle.nickname}
            </h2>
            <p className="text-muted-foreground">
              {[vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(" ")}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {mods.length === 0 ? (
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 bg-zinc-900 rounded-sm flex items-center justify-center mb-4">
                <Package className="w-8 h-8 text-zinc-600" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">
                No Mods Yet
              </h3>
              <p className="text-muted-foreground mb-6 max-w-sm">
                Start documenting your build by adding modifications
              </p>
              <Button
                onClick={() => navigate(`/vehicles/${id}/mods/new`)}
                data-testid="add-first-mod-btn"
                className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Mod
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid gap-4 sm:grid-cols-3 mb-6">
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/10 rounded-sm flex items-center justify-center">
                    <Package className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Total Mods</p>
                    <p className="text-xl font-mono font-bold">{mods.length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/10 rounded-sm flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Total Invested</p>
                    <p className="text-xl font-mono font-bold">${totalCost.toLocaleString()}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center">
                    <Gauge className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Categories</p>
                    <p className="text-xl font-mono font-bold">
                      {new Set(mods.map((m) => m.category)).size}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search & Filters */}
            <Card className="card-tactical bg-zinc-950/50 border-zinc-800 mb-6">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Search */}
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search mods..."
                      data-testid="mod-search-input"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-zinc-900 border-zinc-800"
                    />
                  </div>

                  {/* Category Filter */}
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger
                      data-testid="mod-category-filter"
                      className="w-full sm:w-52 bg-zinc-900 border-zinc-800"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Tag Filter */}
                  <Select value={tagFilter} onValueChange={setTagFilter}>
                    <SelectTrigger
                      data-testid="mod-tag-filter"
                      className="w-full sm:w-36 bg-zinc-900 border-zinc-800"
                    >
                      <SelectValue placeholder="Tag" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      <SelectItem value="all">All Tags</SelectItem>
                      {availableTags.map((tag) => (
                        <SelectItem key={tag} value={tag}>
                          {tag}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Clear Filters */}
                  {hasActiveFilters && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                      data-testid="clear-mod-filters-btn"
                      className="border-zinc-800 hover:bg-zinc-800"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>

                {/* Filter Results Count */}
                {hasActiveFilters && (
                  <p className="text-sm text-muted-foreground mt-3">
                    Showing {filteredMods.length} of {mods.length} mods
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Mods by Category */}
            {filteredMods.length === 0 ? (
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-8 text-center">
                  <p className="text-muted-foreground">No mods match your filters</p>
                  <Button
                    variant="link"
                    onClick={clearFilters}
                    className="text-primary mt-2"
                  >
                    Clear filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {categories.map((category) => {
                  const categoryMods = modsByCategory[category];
                  if (!categoryMods || categoryMods.length === 0) return null;

                  return (
                    <Card key={category} className="card-tactical bg-zinc-950/50 border-zinc-800">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center justify-between">
                          <span>{category}</span>
                          <span className="text-sm font-mono text-muted-foreground">
                            {categoryMods.length} {categoryMods.length === 1 ? "mod" : "mods"}
                          </span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="divide-y divide-zinc-800">
                          {categoryMods.map((mod) => (
                            <div key={mod.id}>
                            <Link
                              to={`/vehicles/${id}/mods/${mod.id}/edit`}
                              data-testid={`mod-item-${mod.id}`}
                              className="flex items-center justify-between p-4 hover:bg-zinc-900/50 transition-colors group"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <p className="font-medium text-foreground truncate">
                                    {mod.item_name}
                                  </p>
                                  {mod.tags?.map((tag) => (
                                    <TagBadge key={tag} tag={tag} />
                                  ))}
                                  {mod.photos?.length > 0 && (
                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                      <Image className="w-3 h-3" />
                                      {mod.photos.length}
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                                  {mod.brand && (
                                    <span className="bg-zinc-800 px-2 py-0.5 rounded-sm">
                                      {mod.brand}
                                    </span>
                                  )}
                                  {mod.part_number && (
                                    <span className="font-mono">#{mod.part_number}</span>
                                  )}
                                  {mod.install_date && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {new Date(mod.install_date + "T00:00:00").toLocaleDateString()}
                                    </span>
                                  )}
                                  {mod.install_mileage > 0 && (
                                    <span className="flex items-center gap-1 font-mono">
                                      <Gauge className="w-3 h-3" />
                                      {mod.install_mileage.toLocaleString()} mi
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                {mod.cost > 0 && (
                                  <span className="text-sm font-mono text-muted-foreground">
                                    ${mod.cost.toLocaleString()}
                                  </span>
                                )}
                                {mod.vendor_link && (
                                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                                )}
                                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground" />
                              </div>
                            </Link>
                            {/* Photo Gallery Strip */}
                            {mod.photos?.length > 0 && (
                              <div className="flex gap-2 px-4 pb-3 overflow-x-auto">
                                {mod.photos.map((photo, pi) => (
                                  <img
                                    key={pi}
                                    src={photo}
                                    alt={`${mod.item_name} photo ${pi + 1}`}
                                    className="w-16 h-16 rounded-md object-cover border border-border flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setLightboxPhoto(photo);
                                    }}
                                    data-testid={`mod-photo-${mod.id}-${pi}`}
                                  />
                                ))}
                              </div>
                            )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      {/* Photo Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center cursor-pointer"
          onClick={() => setLightboxPhoto(null)}
          data-testid="photo-lightbox"
        >
          <img
            src={lightboxPhoto}
            alt="Mod photo"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
}
